<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import BoardEditorModal from '@/components/BoardEditorModal.vue';
import LoginModal from '@/components/LoginModal.vue';
import PinEditorModal from '@/components/PinEditorModal.vue';
import SignUpModal from '@/components/SignUpModal.vue';
import UserAvatar from '@/components/UserAvatar.vue';
import { useAuthStore } from '@/stores/auth';
import { useBoardsStore } from '@/stores/boards';
import { useModalStore } from '@/stores/modals';
import { useUiStore } from '@/stores/ui';

const auth = useAuthStore();
const boards = useBoardsStore();
const ui = useUiStore();
const modals = useModalStore();
const route = useRoute();
const router = useRouter();

const menuOpen = ref(false);
const query = ref('');
const menuRoot = ref(null);
const menuButton = ref(null);
const menuPanel = ref(null);

// Chiudere il menu a ogni navigazione: resterebbe aperto sopra la pagina nuova.
watch(() => route.fullPath, () => { menuOpen.value = false; });

const me = computed(() => auth.username);

/* Perché DUE barre e non una.
 *
 * L'app ha due finestre — "feed" (pinterest.com con lo script iniettato) e
 * "archivio" (questa SPA) — e sono due contesti web separati: nessun DOM in
 * comune, nessun componente condivisibile. La barra non può quindi essere
 * scritta una volta sola. La gemella di questa sta in
 * bridge/bacheca-pinterest.js (cerca CSS_BARRA e costruisciBarra): misure,
 * colori e ordine degli elementi vanno tenuti allineati a mano, perché è
 * l'unica cosa che, al cambio finestra, dice all'utente che è ancora nella
 * stessa applicazione. */

/* I tasti di vista (Feed / Archivio) non stanno più qui: il passaggio al feed
 * vive nella colonna a sinistra (SiteSidebar), che è contenuto della pagina e
 * quindi esiste solo di qua. La gemella su Pinterest invece i suoi tasti li
 * tiene, perché di là una colonna nostra non c'è. */

function submitSearch() {
  const text = query.value.trim();
  if (!text) return;
  router.push({ name: 'search', query: { q: text } });
}

async function createPin() {
  const saved = await modals.open(PinEditorModal, { pin: null });
  // La griglia si ricarica navigando: più semplice e più prevedibile di un
  // event bus globale, che nell'originale non veniva mai deiscritto.
  if (saved) router.push({ name: 'pin', params: { pinId: saved.id } });
}

async function createBoard() {
  const board = await modals.open(BoardEditorModal, { board: null });
  if (board && me.value) {
    boards.reload();
    // Se la griglia delle proprie board è già a schermo, navigarci non la
    // ricarica: il segnale sì, ed è l'unico modo di vederci la board appena
    // fatta senza ricaricare la pagina.
    boards.segnalaMutazione('creata', board);
    router.push({ name: 'boards-by-user', params: { username: me.value } });
  }
}

async function logOut() {
  await auth.logOut();
  router.push({ name: 'home' });
}

/* Il menu raccoglie tutto ciò che prima stava in fila nella barra: in 52px
 * non ci sta, e ogni voce in più competerebbe con la ricerca. */
function toggleMenu() {
  menuOpen.value = !menuOpen.value;
  if (!menuOpen.value) return;
  // Il fuoco entra nel pannello appena si apre, altrimenti da tastiera
  // bisognerebbe attraversare a ritroso tutta la pagina per raggiungerlo.
  nextTick(() => menuPanel.value?.querySelector('a, button, select')?.focus());
}

function closeMenu({ restoreFocus = false } = {}) {
  if (!menuOpen.value) return;
  menuOpen.value = false;
  // Dopo Escape il fuoco deve tornare da dove è partito, non finire sul body.
  if (restoreFocus) nextTick(() => menuButton.value?.focus());
}

// Una voce che agisce chiude il pannello; lingua e tema no, perché sono
// regolazioni che si vogliono vedere applicate restando lì.
function esegui(azione) {
  closeMenu();
  azione();
}

function clicFuori(evento) {
  if (!menuRoot.value?.contains(evento.target)) closeMenu();
}

// L'ascoltatore globale vive solo mentre il pannello è aperto.
watch(menuOpen, (aperto) => {
  if (aperto) document.addEventListener('mousedown', clicFuori);
  else document.removeEventListener('mousedown', clicFuori);
});

onBeforeUnmount(() => document.removeEventListener('mousedown', clicFuori));
</script>

<template>
  <!-- `data-tauri-drag-region` vale SOLO sull'elemento che lo porta, non sui
       figli: per questo sta sulla barra, sul contenitore interno (che la
       copre tutta, quindi senza l'attributo annullerebbe il trascinamento) e
       sullo spaziatore centrale, che è la zona larga e vuota dove la finestra
       si afferra davvero. -->
  <header class="barra" data-tauri-drag-region>
    <!-- `wrap--full` resta: toglie il tetto di 1680px, così la barra si
         allinea alla griglia dei pin che va a tutta larghezza. I margini
         laterali però qui li detta la finestra, non la griglia (vedi CSS). -->
    <div class="wrap wrap--full barra__inner" data-tauri-drag-region>
      <RouterLink class="marchio" :to="{ name: 'home' }">Bacheca</RouterLink>

      <div class="spazio" data-tauri-drag-region></div>

      <!-- La ricerca cerca dentro Bacheca, non su Pinterest: è l'archivio che
           si interroga da qui. Di là la gemella fa l'opposto, e il segnaposto
           lo dice per non lasciare l'ambiguità all'utente. -->
      <form class="cerca" role="search" @submit.prevent="submitSearch">
        <input
          v-model="query"
          class="cerca__campo"
          type="search"
          :placeholder="$t('searchArchivePlaceholder')"
          :aria-label="$t('searchArchivePlaceholder')"
        />
      </form>

      <div ref="menuRoot" class="menu" @keydown.escape="closeMenu({ restoreFocus: true })">
        <button
          id="tasto-menu-bacheca"
          ref="menuButton"
          class="tasto tasto--icona"
          type="button"
          :aria-expanded="menuOpen"
          aria-haspopup="true"
          aria-controls="pannello-menu-bacheca"
          :aria-label="$t('openMenu')"
          @click="toggleMenu"
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" stroke-width="1.6" fill="none" />
          </svg>
        </button>

        <!-- Non è `role="menu"`: contiene una select, e le semantiche di menu
             ARIA imporrebbero la navigazione a frecce fra soli menuitem,
             rompendo il controllo nativo. È un pannello a comparsa, e il Tab
             ci passa dentro nell'ordine naturale. -->
        <div
          v-if="menuOpen"
          id="pannello-menu-bacheca"
          ref="menuPanel"
          class="pannello"
          aria-labelledby="tasto-menu-bacheca"
        >
          <template v-if="auth.isAuthenticated">
            <RouterLink
              class="voce voce--io"
              :to="{ name: 'profile', params: { username: me } }"
            >
              <UserAvatar :username="me" :size="24" />
              <span>{{ me }}</span>
            </RouterLink>

            <hr class="filetto" />

            <!-- Le due azioni e le due destinazioni avevano le stesse parole
                 ("Pin / Board / Pin / Board"): indistinguibili senza i menu
                 etichettati che c'erano nell'originale. Ognuna dice cosa fa. -->
            <button class="voce" type="button" @click="esegui(createPin)">
              <svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true">
                <path d="M7 2v10M2 7h10" stroke="currentColor" stroke-width="1.8" fill="none" />
              </svg>
              {{ $t('newPinButton') }}
            </button>
            <button class="voce" type="button" @click="esegui(createBoard)">
              {{ $t('newBoardButton') }}
            </button>
            <RouterLink
              class="voce"
              :to="{ name: 'pins-by-user', params: { username: me } }"
            >
              {{ $t('myPinsLink') }}
            </RouterLink>
            <RouterLink
              class="voce"
              :to="{ name: 'boards-by-user', params: { username: me } }"
            >
              {{ $t('myBoardsLink') }}
            </RouterLink>

            <hr class="filetto" />
          </template>

          <!-- Il selettore di lingua è stato tolto dal menu: l'app la usa una
               persona sola, che la lingua non la cambia. L'infrastruttura
               multilingua resta (i18n, i quattro file di traduzione, la
               preferenza salvata), quindi rimetterlo è aggiungere un select —
               ma non occupa più spazio in un menu dove ogni voce si paga. -->
          <button class="voce" type="button" @click="ui.toggleTheme">
            <svg v-if="ui.theme === 'dark'" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <circle cx="10" cy="10" r="3.6" fill="currentColor" />
              <path
                d="M10 2v2M10 16v2M2 10h2M16 10h2M4.4 4.4l1.4 1.4M14.2 14.2l1.4 1.4M15.6 4.4l-1.4 1.4M5.8 14.2l-1.4 1.4"
                stroke="currentColor"
                stroke-width="1.5"
              />
            </svg>
            <svg v-else viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M16 12.3A6.8 6.8 0 017.7 4a6.8 6.8 0 108.3 8.3z" fill="currentColor" />
            </svg>
            {{ $t('themeToggle') }}
          </button>

          <template v-if="auth.isAuthenticated">
            <hr class="filetto" />
            <button class="voce" type="button" @click="esegui(logOut)">
              {{ $t('logOutLink') }}
            </button>
          </template>
          <template v-else-if="auth.ready">
            <hr class="filetto" />
            <button class="voce" type="button" @click="esegui(() => modals.open(SignUpModal))">
              {{ $t('signUpLink') }}
            </button>
            <button class="voce voce--primaria" type="button" @click="esegui(() => modals.open(LoginModal))">
              {{ $t('logInLink') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
/* Qui NON si usano i token del tema, di proposito.
 *
 * Questa barra è la cornice della finestra, non contenuto della pagina, e deve
 * combaciare con la gemella iniettata in Pinterest (CSS_BARRA in
 * bridge/bacheca-pinterest.js), che è scura sempre perché di là il tema chiaro
 * non esiste. Se seguisse il tema dell'app, passando in tema chiaro la cornice
 * cambierebbe colore in una finestra e non nell'altra: esattamente il salto
 * che queste due barre esistono per evitare. Quindi colori letterali, identici
 * a quelli di là, in entrambi i temi. */

.barra {
  position: sticky;
  top: 0;
  z-index: 40;
  /* 52px non è una preferenza: è la misura della gemella. */
  height: 52px;
  box-sizing: border-box;
  border-bottom: 1px solid #2a2d34;
  background: #17181c;
  color: #e8e8ea;
  /* Stessa pila di caratteri della gemella: di là non c'è il font dell'app. */
  font: 500 13px/1 -apple-system, system-ui, sans-serif;
}

.barra__inner {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 100%;
  /* 92px a sinistra perché la finestra ha la titlebar trasparente sovrapposta
   * (TitleBarStyle::Overlay) con i semafori macOS riposizionati a (18,18):
   * senza questo margine il marchio finirebbe sotto i tre bottoni. Vince sul
   * padding-inline di `.wrap` per specificità, e va lasciato vincere. */
  padding-left: 92px;
  padding-right: 14px;
}

.marchio {
  color: #9b9ea8;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
}

.marchio:hover {
  color: #e8e8ea;
}

.tasto {
  padding: 6px 11px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #9b9ea8;
  font: inherit;
  cursor: pointer;
}

.tasto:hover {
  background: #21242a;
  color: #e8e8ea;
}

.tasto[aria-current='true'] {
  background: #2a2d34;
  color: #fff;
}

.tasto[aria-expanded='true'] {
  background: #2a2d34;
  color: #e8e8ea;
}

.tasto--icona {
  display: grid;
  place-items: center;
  padding: 6px 8px;
}

/* Flessibile e vuoto: è la presa per trascinare la finestra. Si allunga con
 * `align-self: stretch` a tutta l'altezza, così la zona utile è un rettangolo
 * di 52px e non una striscia sottile. */
.spazio {
  flex: 1;
  align-self: stretch;
}

.cerca__campo {
  width: min(20rem, 34vw);
  padding: 6px 10px;
  border: 1px solid #2a2d34;
  border-radius: 8px;
  background: #121317;
  color: inherit;
  font: inherit;
}

.cerca__campo:focus {
  border-color: #6fa3de;
  outline: none;
}

.menu {
  position: relative;
}

.pannello {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 13rem;
  padding: 6px;
  border: 1px solid #2a2d34;
  border-radius: 10px;
  background: #17181c;
  box-shadow: 0 1px 2px rgb(0 0 0 / 40%), 0 8px 24px rgb(0 0 0 / 32%);
}

.voce {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 10px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #9b9ea8;
  font: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

.voce:hover,
.voce:focus-visible {
  background: #21242a;
  color: #e8e8ea;
}

.voce.router-link-active {
  color: #e8e8ea;
}

.voce--io {
  color: #e8e8ea;
  font-weight: 600;
}

.voce--riga {
  justify-content: space-between;
  cursor: default;
}

.voce--riga:hover {
  background: transparent;
  color: #9b9ea8;
}

.voce--primaria {
  background: #2a2d34;
  color: #fff;
}

.voce__etichetta {
  cursor: default;
}

.scelta {
  padding: 3px 6px;
  border: 1px solid #2a2d34;
  border-radius: 6px;
  background: #121317;
  color: #e8e8ea;
  font: inherit;
  cursor: pointer;
}

.filetto {
  height: 0;
  margin: 4px 0;
  border: 0;
  border-top: 1px solid #2a2d34;
}

/* Fuori dall'app la barra è comunque stretta: si sacrifica prima la ricerca,
 * poi il marchio, mai il menu. */
@media (max-width: 640px) {
  .cerca {
    display: none;
  }
}

@media (max-width: 460px) {
  .marchio {
    display: none;
  }
}
</style>
